import type { Route } from './+types/services.biweekly';
import { Link } from 'react-router';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { SITE, PRICING } from '~/data/site';
import { useBranding } from '~/lib/branding';

export const meta: Route.MetaFunction = () => [
  ...buildMeta({
    title: 'Biweekly Cleaning Service | Tampa Bay',
    description:
      `Professional biweekly home cleaning in Tampa Bay. Every two weeks, same crew. Per-square-foot pricing starts at ${PRICING.biweeklyRate.toFixed(2)}/sqft. ${PRICING.minimum} minimum. Schedule your first clean today.`,
    path: '/biweekly',
    jsonLd: [
      schemaFor('/biweekly'),
      breadcrumbSchema(breadcrumbSchemaItems('/biweekly')),
    ].filter((s): s is Record<string, unknown> => !!s),
  }),
];

export default function BiweeklyServicePage() {
  const branding = useBranding();
  const examplePrice = 2400 * PRICING.biweeklyRate;

  return (
    <main id="main-content">
      <Breadcrumbs path="/biweekly" pageLabel="Biweekly Cleaning" />
      {/* ── HEADER ───────────────────────────────────────── */}
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Recurring Service</p>
          <h1 className="display-lg page-h1-full">
            Clean every two weeks,
            <em className="italic-display">the sensible middle ground.</em>
          </h1>
          <p className="lede chapter-lede">
            Biweekly cleaning keeps your home fresh without the weekly commitment. Your crew
            arrives every other week, same team each time. Between visits, your home stays
            clean and manageable.
          </p>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">How much does it cost?</h2>
          <div className="price-example price-example-mt reveal">
            <h3 className="display-sm text-bone">${PRICING.biweeklyRate.toFixed(2)} per square foot</h3>
            <div className="price-ex-line price-ex-block-mt">
              <span>Example: 2,400 sqft home<br /><small className="text-mute">Your actual price depends on your home&rsquo;s size.</small></span>
              <span className="price-ex-total price-ex-total-sm">${Math.round(examplePrice)}</span>
            </div>
            <div className="price-ex-line">
              <span>Minimum on every service<br /><small className="text-mute">Even your smallest visits are a full professional clean.</small></span>
              <span className="price-ex-total price-ex-total-sm">${PRICING.minimum}</span>
            </div>
            <p className="marginalia price-example-marginalia">
              See your exact price for your home in under 60 seconds.
            </p>
          </div>
          <div className="section-cta-left">
            <Link to="/quote" className="btn btn-brass">Calculate your price →</Link>
          </div>
        </div>
      </section>

      {/* ── WHY BIWEEKLY ──────────────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">The biweekly sweet spot</p>
          <h2 className="display-md">Rhythm meets reality.</h2>
          <div className="feature-list reveal">
            <div className="feature-item">
              <h3 className="feature-title">Same crew every visit</h3>
              <p className="feature-text">
                Two-week intervals mean your cleaners deeply understand your home. They
                remember. They anticipate. They care about the quality.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Your home stays fresh</h3>
              <p className="feature-text">
                Two weeks is the sweet spot: long enough to feel the benefit of
                professional cleaning, short enough that your home never feels neglected.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">More manageable than weekly</h3>
              <p className="feature-text">
                You save money. You still get the consistency and care that schedule changes can't
                provide. Most homes choose biweekly. Most for good reason.
              </p>
            </div>
            <div className="feature-item">
              <h3 className="feature-title">Rotating deep attention</h3>
              <p className="feature-text">
                Each visit has a focus area: baseboards, cabinets, trim, windows. Nothing
                goes neglected. Your home gets comprehensively clean.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ───────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">What we clean every visit</h2>
          <div className="checklist-grid reveal">
            <div className="checklist-col">
              <h3 className="checklist-heading">Kitchen</h3>
              <ul className="checklist">
                <li>Countertops and backsplash</li>
                <li>Sink and faucet</li>
                <li>Appliances (exterior)</li>
                <li>Cabinet fronts and handles</li>
                <li>Baseboards</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bathrooms</h3>
              <ul className="checklist">
                <li>Toilets and sinks</li>
                <li>Mirrors and fixtures</li>
                <li>Shower/tub clean</li>
                <li>Floors and grout</li>
                <li>Ventilation fans</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Living Spaces</h3>
              <ul className="checklist">
                <li>Vacuuming and mopping</li>
                <li>Dusting surfaces</li>
                <li>Light switches and doors</li>
                <li>Windows (interior)</li>
                <li>Baseboards and trim</li>
              </ul>
            </div>
            <div className="checklist-col">
              <h3 className="checklist-heading">Bedrooms</h3>
              <ul className="checklist">
                <li>Floors vacuumed</li>
                <li>Dusting all surfaces</li>
                <li>Organizing clutter</li>
                <li>Baseboards</li>
                <li>Light switches</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── FIRST VISIT UPGRADE ──────────────────────────── */}
      <section className="section-sm">
        <div className="container-narrow">
          <h2 className="display-md">Launch with a deep clean.</h2>
          <p className="lede chapter-lede">
            Start with an upgrade on your first biweekly visit: <strong>+${PRICING.deepUpgradeRate.toFixed(2)}/sqft</strong>.
            That&rsquo;s when we reset your home completely &mdash; baseboards, inside cabinets,
            behind appliances, all the spaces monthly visits won&rsquo;t touch. After that,
            your crew rotates attention so every corner gets deep care.
          </p>
          <p className="lede chapter-lede chapter-lede-mt">
            Example: 2,400 sqft home, first visit would be ${Math.round(examplePrice + (2400 * PRICING.deepUpgradeRate))}.
          </p>
        </div>
      </section>

      {/* ── WHY NLC ────────────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Why {branding.name}</p>
          <h2 className="display-md">Built for <em className="italic-display">relationship.</em></h2>
          <div className="value-props reveal">
            <div className="value-prop">
              <h3>W-2 Employees Only</h3>
              <p>Your crew is employed by us. Benefits, training, accountability — all real.</p>
            </div>
            <div className="value-prop">
              <h3>Insured &amp; Background Checked</h3>
              <p>Every technician vetted. Insurance covers your home. You have recourse.</p>
            </div>
            <div className="value-prop">
              <h3>Per-Square-Foot Pricing</h3>
              <p>No surprises. No upsells. The price is the price, every two weeks.</p>
            </div>
            <div className="value-prop">
              <h3>Continuity Over Transaction</h3>
              <p>Your team stays yours. They learn. They anticipate. They take pride.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <RelatedPages path="/biweekly" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready to start?</p>
          <h2>Schedule your first biweekly clean</h2>
          <p className="cta-lede">See your price for your home. No obligation. Takes 60 seconds.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">Get your price →</Link>
        </div>
      </section>
    </main>
  );
}
